import { ValueEncoder } from "../Utils/ValueEncoder";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { MonitorType } from "../Core/MonitorType";
import { Device } from "../Core/Device";
import { Guid } from "../Utils/Guid";
import {
	MonitorSettingsBase, MonitorSettings_Ping, MonitorSettings_SnmpIf, MonitorSettings_SnmpIfEntry,
	MonitorSettings_Config, MonitorSettings_Snmp, SnmpMonitorEntry, MonitorPolicy,
	MonitorSettings_PRTG
} from "../Monitors";
import { IfTable, IfEntry } from "../InfoSets/IfTable";
import { List } from "../Utils";


export class MonitorEntry implements ObjectSerializer {

	public MonitorType: MonitorType;

	public MonitorPolicyId: Guid;
	public get HasMonitorPolicyId(): boolean {
		return (!!this.MonitorPolicyId && !this.MonitorPolicyId.Equals(Guid.Empty));
	}

	public Name: string;
	public get HasName(): boolean {
		return !!this.Name;
	}

	public Settings: MonitorSettingsBase;
	public get HasSettings(): boolean {
		return !!this.Settings;
	}

	public LastPollTime: string;
	public get HasLastPollTime(): boolean {
		return !!this.LastPollTime;
	}

	constructor() {
		this.MonitorType = MonitorType.None;
		this.MonitorPolicyId = Guid.Empty;
		this.Name = "";
		this.LastPollTime = "";
		this.Settings = null;
	}

	public static Create_MonitorType(monType: MonitorType): MonitorEntry {
		let entry = new MonitorEntry();

		entry.MonitorType = monType;
		if (monType != MonitorType.None) {
			entry.Settings = MonitorSettingsBase.CreateInstance(monType);
		}

		return entry;
	}

	public static Create_MonitorEntry(monEntry: MonitorEntry): MonitorEntry {
		let entry = new MonitorEntry();

		if (monEntry) {
			entry.CopyConfig_MonitorEntry(monEntry);
		}

		return entry;
	}

	public static Create_MonitorSettings(monSettings: MonitorSettingsBase, deviceGuid: Guid): MonitorEntry {
		console.assert(!!monSettings);

		let entry = new MonitorEntry();

		entry.MonitorType = monSettings.MonitorType;
		entry.MonitorPolicyId = monSettings.MonitorPolicyId;

		switch (monSettings.MonitorType) {
			case MonitorType.Ping:
				entry.Settings = entry.CreatePingSettings_MonitorSettings(monSettings);
				break;

			case MonitorType.SNMP_Interface:
				entry.Settings = entry.CreateSnmpIfSettings_MonitorSettings(monSettings);
				break;

			case MonitorType.Config:
				entry.Settings = entry.CreateConfigSettings_MonitorSettings(monSettings);
				break;

			case MonitorType.SNMP:
				entry.Settings = entry.CreateSnmpSettings_MonitorSettings(monSettings, deviceGuid);
				break;

			default:
				console.assert(false, "Unsupported monitor type: " + monSettings.MonitorType);
				break;
		}

		return entry;
	}

	public static Create_MonitorPolicy(monPolicy: MonitorPolicy, device: Device): MonitorEntry {
		console.assert(!!monPolicy);

		let entry = new MonitorEntry();

		entry.MonitorType = monPolicy.MonitorType;
		entry.MonitorPolicyId = monPolicy.MonitorPolicyId;
		entry.Name = monPolicy.Name;

		switch (monPolicy.MonitorType) {
			case MonitorType.Ping:
				entry.Settings = entry.CreatePingSettings_MonitorPolicy(monPolicy, device);
				break;

			case MonitorType.SNMP_Interface:
				entry.Settings = entry.CreateSnmpIfSettings_MonitorPolicy(monPolicy, device);
				break;

			case MonitorType.Config:
				entry.Settings = entry.CreateConfigSettings_MonitorPolicy(monPolicy, device);
				break;

			case MonitorType.SNMP:
				entry.Settings = entry.CreateSnmpSettings_MonitorPolicy(monPolicy, device);
				break;

			default:
				console.assert(false, "Unsupported monitor type: " + monPolicy.MonitorType);
				break;
		}

		return entry;
	}

	private CreatePingSettings_MonitorSettings(monSettings: MonitorSettingsBase): MonitorSettings_Ping {

		let newSettings = new MonitorSettings_Ping();

		if (!!monSettings) {
			newSettings.Copy(monSettings);
		}

		return newSettings;
	}

	private CreatePingSettings_MonitorPolicy(monPolicy: MonitorPolicy, monitoredDevice: Device): MonitorSettings_Ping {

		let newSettings = new MonitorSettings_Ping();

		if (!!monPolicy && monPolicy.HasSettings) {
			let policySettings = monPolicy.Settings as MonitorSettings_Ping;
			if (!!policySettings) {
				newSettings.Copy(policySettings);
				newSettings.Clear();

				if (!!monitoredDevice) {
					if (monitoredDevice.HasIpAddress) {
						newSettings.Add_Ip(monitoredDevice.IpAddress);
					}
				}
			}
		}

		return newSettings;
	}

	private CreateSnmpIfSettings_MonitorSettings(monSettings: MonitorSettingsBase): MonitorSettings_SnmpIf {

		let newSettings = new MonitorSettings_SnmpIf();

		if (!!monSettings) {
			newSettings.Copy(monSettings);
		}

		return newSettings;
	}

	private CreateSnmpIfSettings_MonitorPolicy(monPolicy: MonitorPolicy, monitoredDevice: Device): MonitorSettings_SnmpIf {

		let newSettings = new MonitorSettings_SnmpIf();

		if (!!monPolicy && monPolicy.HasSettings) {
			let policySettings = monPolicy.Settings as MonitorSettings_SnmpIf;
			if (!!policySettings) {
				newSettings.Copy(policySettings);
			}
		}

		return newSettings;
	}

	private CreateConfigSettings_MonitorSettings(monSettings: MonitorSettingsBase): MonitorSettings_Config {

		let newSettings = new MonitorSettings_Config();

		if (!!monSettings) {
			newSettings.Copy(monSettings);
		}

		return newSettings;
	}

	private CreateSnmpSettings_MonitorSettings(monSettings: MonitorSettingsBase, deviceGuid: Guid): MonitorSettings_Snmp {

		let newSettings = new MonitorSettings_Snmp();

		if (!!monSettings) {
			newSettings.Copy(monSettings);
			this.RemoveSnmpMonitorEntriesNotForDevice(deviceGuid);
		}

		return newSettings;
	}

	private CreateConfigSettings_MonitorPolicy(monPolicy: MonitorPolicy, monitoredDevice: Device): MonitorSettings_Config {

		let newSettings = new MonitorSettings_Config();

		if (!!monPolicy && monPolicy.HasSettings) {
			let policySettings = monPolicy.Settings as MonitorSettings_Config;
			if (!!policySettings) {
				newSettings.Copy(policySettings);
			}
		}

		return newSettings;
	}

	private CreateSnmpSettings_MonitorPolicy(monPolicy: MonitorPolicy, monitoredDevice: Device): MonitorSettings_Snmp {

		let newSettings = new MonitorSettings_Snmp();

		if (!!monPolicy && monPolicy.HasSettings) {
			let policySettings = monPolicy.Settings as MonitorSettings_Snmp;
			if (!!policySettings) {
				newSettings.Copy(policySettings);
				this.RemoveSnmpMonitorEntriesNotForDevice(monitoredDevice.GUID);
			}
		}

		return newSettings;
	}

	private CreatePrtgSettings_MonitorSettings(monSettings: MonitorSettingsBase): MonitorSettings_PRTG {

		let newSettings = new MonitorSettings_PRTG();

		if (!!monSettings) {
			newSettings.Copy(monSettings);
		}

		return newSettings;
	}

	public AddPingMonitor(monitorPolicy: MonitorPolicy, monitoredDevice: Device): boolean {
		console.assert(!!monitorPolicy);
		console.assert(!!monitoredDevice);
		console.assert(this.MonitorType == MonitorType.Ping);

		if (!monitorPolicy)
			return false;
		if (!monitoredDevice)
			return false;

		let pingMonitorSettings: MonitorSettings_Ping = this.GetMonitorSettings_Ping();
		if (!!pingMonitorSettings) {
			if (monitoredDevice.HasIpAddress) {
				pingMonitorSettings.Add_Ip(monitoredDevice.IpAddress);
				return true;
			}
		}

		return false;
	}

	public AddSnmpIfMonitor(monitorPolicy: MonitorPolicy, monitoredDevice: Device, ifIndex: number): boolean {
		console.assert(!!monitorPolicy);
		console.assert(!!monitoredDevice);
		console.assert(this.MonitorType == MonitorType.SNMP_Interface);

		if (!monitorPolicy)
			return false;
		if (!monitoredDevice)
			return false;
		if (!monitoredDevice.HasIfTable)
			return false;

		let ifEntry: IfEntry = monitoredDevice.IfTable.Find(ifIndex);
		if (!ifEntry)
			return false;

		let snmpIfMonitorSettings: MonitorSettings_SnmpIf = this.GetMonitorSettings_SnmpIf();
		if (!!snmpIfMonitorSettings) {
			let monIfEntry: MonitorSettings_SnmpIfEntry = snmpIfMonitorSettings.Add_IfIndex(ifEntry.IfIndex);
			if (!!monIfEntry) {
				monIfEntry.Name = ifEntry.ShortDisplayName;
				return true;
			}
		}

		return false;
	}

	public AddSnmpIfMonitors(monitorPolicy: MonitorPolicy, monitoredDevice: Device, addUpOnly: boolean = true): number {
		console.assert(!!monitorPolicy);
		console.assert(!!monitoredDevice);
		console.assert(this.MonitorType == MonitorType.SNMP_Interface);

		if (!monitorPolicy)
			return 0;
		if (!monitoredDevice)
			return 0;

		let addCount = 0;

		let snmpIfMonitorSettings: MonitorSettings_SnmpIf = this.GetMonitorSettings_SnmpIf();
		if (!!snmpIfMonitorSettings) {
			if (monitoredDevice.HasIfTable) {
				let ifTable: IfTable = monitoredDevice.IfTable;

				ifTable.ForEach((ifEntry: IfEntry) => {
					let addIf = true;

					if (addUpOnly) {
						if (!ifEntry.IsOperStatusUp || !ifEntry.IsLinkable || !ifEntry.HasInOutOctetTraffic) {
							addIf = false;
						}
					}

					if (addIf) {
						let monIfEntry: MonitorSettings_SnmpIfEntry = snmpIfMonitorSettings.Add_IfIndex(ifEntry.IfIndex);
						if (!!monIfEntry) {
							monIfEntry.Name = ifEntry.ShortDisplayName;
							addCount++;
						}
					}
				}, this);
			}
		}

		return addCount;
	}

	public AddConfigMonitor(monitorPolicy: MonitorPolicy, monitoredDevice: Device): boolean {
		console.assert(!!monitorPolicy);
		console.assert(!!monitoredDevice);
		console.assert(this.MonitorType == MonitorType.Config);

		if (!monitorPolicy)
			return false;
		if (!monitoredDevice)
			return false;

		let configMonitorSettings: MonitorSettings_Config = this.GetMonitorSettings_Config();
		if (!!configMonitorSettings) {
			configMonitorSettings.Add_Names();
			return true;
		}

		return false;
	}

	public AddUpdateSnmpMonitor(monitorPolicy: MonitorPolicy, deviceGuid: Guid): boolean {
		console.assert(!!monitorPolicy);
		console.assert(this.MonitorType == MonitorType.SNMP);

		if (!monitorPolicy)
			return false;
		if (this.MonitorType != MonitorType.SNMP)
			return false;

		let snmpMonitorSettings: MonitorSettings_Snmp = this.GetMonitorSettings_Snmp();
		if (snmpMonitorSettings) {
			snmpMonitorSettings.CopySettings(monitorPolicy.Settings);
			this.RemoveSnmpMonitorEntriesNotForDevice(deviceGuid);
			return true;
		}

		return false;
	}

	private RemoveSnmpMonitorEntriesNotForDevice(deviceGuid: Guid): void {
		let snmpMonitorSettings: MonitorSettings_Snmp = this.GetMonitorSettings_Snmp();
		if (snmpMonitorSettings) {
			let entriesToRemove = new List<SnmpMonitorEntry>();

			snmpMonitorSettings.ForEach((entry: SnmpMonitorEntry) => {
				if (!entry.DeviceGuid.Equals(deviceGuid)) {
					entriesToRemove.Add(entry);
				}
			});

			entriesToRemove.ForEach((entry: SnmpMonitorEntry) => {
				snmpMonitorSettings.Remove(entry);
			});

			// Verify
			snmpMonitorSettings.ForEach((entry: SnmpMonitorEntry) => {
				console.assert(entry.DeviceGuid.Equals(deviceGuid));
			});
		}
	}

	private SyncMonitorPolicyGuids(): boolean {
		let guidsAdded = false;

		if (!this.HasSettings)
			return guidsAdded;

		// SYNC-up IDs
		if (this.HasMonitorPolicyId && !this.Settings.HasMonitorPolicyId) {
			this.Settings.MonitorPolicyId = this.MonitorPolicyId;
			guidsAdded = true;
		}

		return guidsAdded;
	}

	public Copy(other: MonitorEntry): void {
		if (!other)
			return;

		this.MonitorType = other.MonitorType;
		this.MonitorPolicyId = other.MonitorPolicyId;
		this.Name = other.Name;
		this.LastPollTime = other.LastPollTime;

		if (other.HasSettings) {
			this.Settings = other.Settings.Clone();
		}
		else {
			this.Settings = null;
		}

		this.SyncMonitorPolicyGuids();
	}

	public CopyConfig_MonitorEntry(other: MonitorEntry): void {
		if (!other)
			return;
		console.assert(this != other);
		if (this == other)
			return;

		this.MonitorType = other.MonitorType;
		this.MonitorPolicyId = other.MonitorPolicyId;
		this.Name = other.Name;
		this.LastPollTime = other.LastPollTime;

		if (this.HasSettings && other.HasSettings) {
			if (this.Settings.MonitorType == other.Settings.MonitorType)
				this.Settings.CopyConfig(other.Settings);
			else
				this.Settings = other.Settings.CloneConfig();
		}
		else if (other.HasSettings) {
			this.Settings = other.Settings.CloneConfig();
		}

		this.SyncMonitorPolicyGuids();
	}

	public CopyConfig_MonitorPolicy(otherPolicy: MonitorPolicy, deviceGuid: Guid): void {
		if (!otherPolicy)
			return;

		this.MonitorType = otherPolicy.MonitorType;
		this.MonitorPolicyId = otherPolicy.MonitorPolicyId;
		this.Name = otherPolicy.Name;

		if (this.HasSettings && otherPolicy.HasSettings) {
			if (this.Settings.MonitorType == otherPolicy.Settings.MonitorType)
				this.Settings.CopyConfig(otherPolicy.Settings);
			else
				this.Settings = otherPolicy.Settings.CloneConfig();
		}
		else if (otherPolicy.HasSettings) {
			this.Settings = otherPolicy.Settings.CloneConfig();
		}

		this.RemoveSnmpMonitorEntriesNotForDevice(deviceGuid);

		this.SyncMonitorPolicyGuids();
	}

	public CopySettings(otherPolicy: MonitorPolicy, monitoredDevice: Device): void {
		if (!otherPolicy)
			return;

		this.MonitorType = otherPolicy.MonitorType;
		this.MonitorPolicyId = otherPolicy.MonitorPolicyId;
		this.Name = otherPolicy.Name;

		if (this.HasSettings && otherPolicy.HasSettings) {
			if (this.Settings.MonitorType == otherPolicy.Settings.MonitorType)
				this.Settings.CopySettings(otherPolicy.Settings);
			else
				this.Settings = otherPolicy.Settings.CloneConfig();
		}
		else if (otherPolicy.HasSettings) {
			this.Settings = otherPolicy.Settings.CloneConfig();
		}

		this.RemoveSnmpMonitorEntriesNotForDevice(monitoredDevice.GUID);

		this.SyncMonitorPolicyGuids();
	}

	public get HasGuids(): boolean {
		if (!this.HasSettings)
			return true;

		return this.Settings.HasGuids;
	}

	public VerifyGuids(policySettings: MonitorSettingsBase = null): boolean {
		let guidsAdded = false;

		guidsAdded = this.SyncMonitorPolicyGuids();

		if (policySettings != null) {
			if (this.Settings.VerifyGuids_MonitorSettings(policySettings))
				guidsAdded = true;
		}
		else {
			if (this.Settings.VerifyGuids())
				guidsAdded = true;
		}

		return guidsAdded;
	}

	// UTILITY cast to specific settings
	public GetMonitorSettings_Ping(): MonitorSettings_Ping {
		let monitorSettings: MonitorSettings_Ping = null;

		if (this.HasSettings && (this.MonitorType == MonitorType.Ping))
			monitorSettings = this.Settings as MonitorSettings_Ping;

		return monitorSettings;
	}

	public GetMonitorSettings_SnmpIf(): MonitorSettings_SnmpIf {
		let monitorSettings: MonitorSettings_SnmpIf = null;

		if (this.HasSettings && (this.MonitorType == MonitorType.SNMP_Interface))
			monitorSettings = this.Settings as MonitorSettings_SnmpIf;

		return monitorSettings;
	}

	public GetMonitorSettings_Config(): MonitorSettings_Config {
		let monitorSettings: MonitorSettings_Config = null;

		if (this.HasSettings && (this.MonitorType == MonitorType.Config))
			monitorSettings = this.Settings as MonitorSettings_Config;

		return monitorSettings;
	}

	public GetMonitorSettings_Snmp(): MonitorSettings_Snmp {
		let monitorSettings: MonitorSettings_Snmp = null;

		if (this.HasSettings && (this.MonitorType == MonitorType.SNMP))
			monitorSettings = this.Settings as MonitorSettings_Snmp;

		return monitorSettings;
	}

	public GetMonitorSettings_PRTG(): MonitorSettings_PRTG {
		let monitorSettings: MonitorSettings_PRTG = null;

		if (this.HasSettings && (this.MonitorType == MonitorType.PRTG))
			monitorSettings = this.Settings as MonitorSettings_PRTG;

		return monitorSettings;
	}

	public Write(obj: any, context: any): void {
		obj.monitor_entry = {};
		obj = obj.monitor_entry;

		obj.monitor_type = MonitorType[this.MonitorType];

		if (this.HasMonitorPolicyId)
			obj.monitor_policy_id = ValueEncoder.EncodeGuid(this.MonitorPolicyId);

		if (this.HasName)
			obj.name = this.Name;

		if (this.HasLastPollTime)
			obj.last_poll_time = this.LastPollTime;

		if (this.HasSettings)
			this.Settings.Write(obj, context);
	}

	public Read(obj: any, context: any): void {
		if (!obj.monitor_entry) {
			throw new Error();
		}
		obj = obj.monitor_entry;

		if (obj.monitor_type !== undefined)
			this.MonitorType = MonitorType[<string>obj.monitor_type];

		if (obj.monitor_policy_id !== undefined)
			this.MonitorPolicyId = ValueEncoder.DecodeGuid(obj.monitor_policy_id);

		if (obj.name !== undefined)
			this.Name = obj.name;

		if (obj.last_poll_time !== undefined)
			this.LastPollTime = obj.last_poll_time;

		if (obj.monitor_settings !== undefined) {
			this.Settings = MonitorSettingsBase.CreateInstance(this.MonitorType);
			this.Settings.Read(obj, context);
		}
	}

}
